import React, { useEffect, useRef, useState } from "react";
import {
  Plus,
  MoreHorizontal,
  GripVertical,
  GripHorizontal,
  X,
  Edit3,
  Copy,
  Trash2,
  Tag,
  CheckCircle,
  Circle,
  Star,
  LucideIcon,
} from "lucide-react";
import { useDraggable } from "@dnd-kit/core";
import { availableIcons } from "./configComponents/SmartListConfig";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

// Draggable handle for "drag to gauge" - used when list is linked to an Impact Gauge
function DragToGaugeHandle({
  sourceListKey,
  listItemId,
  text,
}: {
  sourceListKey: string;
  listItemId: number;
  text: string;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `list-item-${sourceListKey}-${listItemId}`,
    data: {
      type: "LIST_ITEM",
      sourceListKey,
      listItemId,
      text,
    },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`cursor-grab active:cursor-grabbing p-1 rounded hover:bg-slate-100 ${isDragging ? "opacity-50" : ""}`}
      title={text}
    >
      <GripHorizontal className="w-4 h-4 text-slate-400" />
    </div>
  );
}

// Type definitions
interface Category {
  id: string;
  name: string;
  color: string;
  textColor: string;
}

interface ListItem {
  id: number;
  text: string;
  categoryId?: string | null;
  completed?: boolean;
  starred?: boolean;

  [key: string]: any; // Allow additional custom properties
}

interface ActionConfig {
  id: string;
  label: string;
  icon: string;
  action: (itemId: number | null) => void;
  dangerous?: boolean;
}

interface SmartListProps {
  config: {
    title?: string;
    initialItems?: (string | Partial<ListItem>)[];
    listActions?: ActionConfig[];
    itemActions?: ActionConfig[];
    categories?: Category[];
    showCategories?: boolean;
    showReorder?: boolean;
    placeholder?: string;
    className?: string;
  };
  userData: object;
  content: { key: string };
  onChange: (key: string, value: any) => void;
  readonly: boolean;
  /** When set, list items can be dragged onto linked Impact Gauges */
  linkedGaugeKeys?: string[];
}

const SmartList: React.FC<SmartListProps> = ({
  config: {
    title = "My List",
    initialItems = [],
    listActions = [],
    itemActions = [],
    categories = [],
    showCategories = true,
    showReorder = true,
    placeholder = "Add an item...",
    className = "",
  } = {},
  userData,
  content,
  onChange,
  readonly = false,
  linkedGaugeKeys = [],
}) => {
  const { t } = useTranslation();

  const canDragToGauge = linkedGaugeKeys.length > 0 && !readonly;

  // Initialize items with proper typing
  const [items, setItems] = useState<ListItem[]>(
    initialItems.map((item, index) => ({
      id: Date.now() + index,
      text: typeof item === "string" ? item : (item as ListItem).text || "",
      categoryId:
        typeof item === "object" ? (item as ListItem).categoryId : null,
      completed:
        typeof item === "object"
          ? (item as ListItem).completed || false
          : false,
      starred:
        typeof item === "object" ? (item as ListItem).starred || false : false,
      ...(typeof item === "object" ? item : {}),
    }))
  );

  // Track the last locally-produced items JSON to distinguish local vs remote changes
  const lastLocalItemsRef = useRef<string>(JSON.stringify(items));
  const isExternalUpdateRef = useRef(false);
  const isFirstRenderRef = useRef(true);

  // Sync from external data (e.g., real-time meeting updates from another participant)
  useEffect(() => {
    const externalItems = (userData as Record<string, any>)?.items;
    if (!externalItems || !Array.isArray(externalItems)) return;

    const externalStr = JSON.stringify(externalItems);
    // Skip if this matches what we last sent (our own echo)
    if (externalStr === lastLocalItemsRef.current) return;

    isExternalUpdateRef.current = true;
    setItems(externalItems);
  }, [userData]);

  const [newItemText, setNewItemText] = useState<string>("");
  const [showListMenu, setShowListMenu] = useState<boolean>(false);
  const [showItemMenu, setShowItemMenu] = useState<number | null>(null);
  const [showCategoryMenu, setShowCategoryMenu] = useState<number | null>(null);
  const [draggedItem, setDraggedItem] = useState<ListItem | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);

  // Default categories if none provided
  const defaultCategories: Category[] = [
    {
      id: "high",
      name: "High Priority",
      color: "bg-red-200",
      textColor: "text-red-800",
    },
    {
      id: "medium",
      name: "Medium Priority",
      color: "bg-yellow-200",
      textColor: "text-yellow-800",
    },
    {
      id: "low",
      name: "Low Priority",
      color: "bg-green-200",
      textColor: "text-green-800",
    },
    {
      id: "idea",
      name: "Idea",
      color: "bg-purple-200",
      textColor: "text-purple-800",
    },
    {
      id: "todo",
      name: "To Do",
      color: "bg-primary/20",
      textColor: "text-primary",
    },
  ];

  const availableCategories: Category[] =
    categories.length > 0 ? categories : defaultCategories;

  useEffect(() => {
    if (isFirstRenderRef.current) {
      // Don't push initialItems back to the parent on mount - if userData wasn't
      // populated yet when this component mounted, initialItems would be [],
      // and writing that back would overwrite previously saved data once
      // userData arrives.
      isFirstRenderRef.current = false;
      lastLocalItemsRef.current = JSON.stringify(items);
      return;
    }
    if (isExternalUpdateRef.current) {
      isExternalUpdateRef.current = false;
      lastLocalItemsRef.current = JSON.stringify(items);
      return;
    }
    if (!readonly) {
      lastLocalItemsRef.current = JSON.stringify(items);
      onChange(content.key, { items: items });
    }
  }, [items]);

  // Action functions
  const clearCompleted = (): void => {
    setItems((prev) => prev.filter((item) => !item.completed));
    setShowListMenu(false);
  };

  const markAllComplete = (): void => {
    setItems((prev) => prev.map((item) => ({ ...item, completed: true })));
    setShowListMenu(false);
  };

  const deleteItem = (itemId: number | null): void => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const editItem = (itemId: number | null): void => {
    if (itemId != null) setEditingItemId(itemId);
  };

  const commitEdit = (itemId: number, newText: string): void => {
    if (newText.trim()) {
      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, text: newText.trim() } : item
        )
      );
    }
    setEditingItemId(null);
  };

  const duplicateItem = (itemId: number | null): void => {
    const itemToDuplicate = items.find((item) => item.id === itemId);
    if (itemToDuplicate) {
      const newItem: ListItem = {
        ...itemToDuplicate,
        id: Date.now(),
        text: `${itemToDuplicate.text} (copy)`,
      };
      setItems((prev) => [...prev, newItem]);
    }
  };

  const toggleStar = (itemId: number | null): void => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, starred: !item.starred } : item
      )
    );
  };
  // Default list actions if none provided
  const defaultListActions: ActionConfig[] = [
    {
      id: "mark-all-complete",
      label: t("mark_all_complete"),
      icon: "check",
      action: markAllComplete,
    },
    {
      id: "clear-completed",
      label: t("delete_completed"),
      icon: "trash",
      dangerous: true,
      action: clearCompleted,
    },
  ];

  // Default item actions if none provided
  const defaultItemActions: ActionConfig[] = [
    { id: "edit", label: t("edit"), icon: "edit", action: editItem },
    {
      id: "duplicate",
      label: t("duplicate"),
      icon: "copy",
      action: duplicateItem,
    },
    {
      id: "toggle-star",
      label: t("toggle_star"),
      icon: "star",
      action: toggleStar,
    },
    {
      id: "delete",
      label: t("delete"),
      icon: "trash",
      action: deleteItem,
      dangerous: true,
    },
  ];

  const finalListActions: ActionConfig[] =
    listActions.length > 0 ? listActions : defaultListActions;
  const finalItemActions: ActionConfig[] =
    itemActions.length > 0 ? itemActions : defaultItemActions;

  const addItem = (): void => {
    if (!newItemText.trim()) return;

    const newItem: ListItem = {
      id: Date.now(),
      text: newItemText.trim(),
      categoryId: null,
      completed: false,
      starred: false,
    };

    setItems((prev) => [...prev, newItem]);
    setNewItemText("");
  };

  const toggleComplete = (itemId: number): void => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, completed: !item.completed } : item
      )
    );
  };

  const categorizeItem = (itemId: number, categoryId: string | null): void => {
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, categoryId } : item))
    );
    setShowCategoryMenu(null);
    setShowItemMenu(null);
  };

  // Drag and drop handlers
  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    item: ListItem
  ): void => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    index: number
  ): void => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragEnd = (): void => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetIndex: number
  ): void => {
    e.preventDefault();
    if (!draggedItem) return;
    const draggedIndex = items.findIndex((item) => item.id === draggedItem.id);
    if (draggedIndex === -1 || draggedIndex === targetIndex) return;
    const newItems = [...items];
    const [removed] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, removed);
    setItems(newItems);
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const getCategoryById = (categoryId: string | null): Category | undefined => {
    if (!categoryId) return undefined;
    return availableCategories.find((cat) => cat.id === categoryId);
  };

  const executeAction = (
    actionConfig: ActionConfig,
    itemId: number | null
  ): void => {
    if (typeof actionConfig.action === "function") {
      actionConfig.action(itemId);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      addItem();
    }
  };

  return (
    <div
      className={`bg-white border border-gray-200 rounded-xl shadow-[0_2px_8px_rgba(79,70,229,0.08)] mt-3 p-3 w-full ${className} ${
        readonly ? "pointer-events-none select-none opacity-95 bg-gray-50/60" : ""
      }`}
    >
      {/* List Header */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold text-gray-900">
          {title}
        </h2>
        {!readonly && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors rounded-lg hover:bg-gray-100 active:scale-[0.97]"
              aria-label="List actions menu"
            >
              <MoreHorizontal className="w-4 h-4 text-gray-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="z-50 w-48 py-2 bg-white border border-gray-200 rounded-lg shadow-lg"
            sideOffset={8}
            align="end"
          >
            {finalListActions.map((action) => (
              <DropdownMenuItem
                key={action.id}
                onSelect={() => executeAction(action, null)}
                className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                  action.dangerous
                    ? "text-red-600 hover:bg-red-50"
                    : "text-gray-700"
                }`}
              >
                {React.createElement(availableIcons[action.icon], {
                  className: "w-6 h-4 text-gray-500",
                })}
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </div>

      {/* Add Item */}
      {!readonly && (
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            placeholder={placeholder}
            className="flex-1 px-3.5 py-2.5 text-base transition-all border border-gray-200 rounded-lg bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 hover:border-gray-300 text-gray-900 placeholder:text-gray-400 outline-none shadow-sm"
            onKeyPress={handleKeyPress}
          />
          <button
            onClick={addItem}
            className="flex items-center gap-2 px-4 py-2.5 text-sm text-white font-medium transition-all bg-primary rounded-lg hover:bg-primary/90 shadow-sm active:scale-[0.97]"
            aria-label="Add item"
          >
            <Plus className="w-4 h-4" />
            {t("add")}
          </button>
        </div>
      )}

      {/* Items List */}
      <div className="space-y-2 min-h-[120px] max-h-64 overflow-y-auto overscroll-contain">
        {items.map((item, index) => {
          const category = getCategoryById(item.categoryId || null);
          const isDraggedOver = dragOverIndex === index;

          return (
            <div
              key={item.id}
              draggable={showReorder}
              onDragStart={(e) => handleDragStart(e, item)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              onDrop={(e) => handleDrop(e, index)}
              className={`group bg-white rounded-lg px-2 py-1.5 transition-all hover:shadow-sm border ${
                isDraggedOver
                  ? "border-primary/40 bg-primary/5 shadow-md"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-1">
                {/* Drag to Gauge handle - when list is linked to Impact Gauge(s) */}
                {canDragToGauge && (
                  <DragToGaugeHandle
                    sourceListKey={content.key}
                    listItemId={item.id}
                    text={item.text}
                  />
                )}
                {/* Drag Handle for reorder */}
                {showReorder && (
                  <div
                    className="p-1 flex items-center justify-center transition-opacity opacity-0 cursor-grab active:cursor-grabbing group-hover:opacity-100 self-center"
                    title="Drag to reorder"
                  >
                    <GripVertical className="w-4 h-4 text-gray-400" />
                  </div>
                )}
                {/* Item Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    {editingItemId === item.id ? (
                      <input
                        type="text"
                        defaultValue={item.text}
                        autoFocus
                        className="flex-1 text-sm bg-transparent border-b border-primary outline-none py-0.5 text-gray-900"
                        onBlur={(e) => commitEdit(item.id, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitEdit(item.id, (e.target as HTMLInputElement).value);
                          if (e.key === 'Escape') setEditingItemId(null);
                        }}
                      />
                    ) : (
                    <p
                      className={`text-sm break-words ${
                        item.completed ? "line-through text-gray-400" : "text-gray-800"
                      }`}
                    >
                      {item.text}
                    </p>
                    )}

                    <div className="flex items-center gap-1">
                      {/* Star */}
                      {item.starred && (
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      )}

                      {/* Category Button/Badge - Always visible and clickable */}
                      {showCategories && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className={`px-2 py-0.5 rounded-full text-xs font-medium transition-all hover:scale-105 max-w-[80px] truncate ${
                                category
                                  ? `${category.color} ${category.textColor}`
                                  : "bg-gray-100 text-gray-500 hover:bg-gray-200 border border-dashed border-gray-300"
                              }`}
                              aria-label={t("categorize")}
                              title={category?.name}
                            >
                              {category ? (
                                category.name
                              ) : (
                                <div className="flex items-center gap-1">
                                  <Tag className="w-3 h-3" />
                                  <span>{t("tag")}</span>
                                </div>
                              )}
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-40 py-2 bg-white border border-gray-200 rounded-lg shadow-lg">
                            <DropdownMenuItem
                              onSelect={() => categorizeItem(item.id, null)}
                              className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50"
                            >
                              {t("remove_category")}
                            </DropdownMenuItem>
                            {availableCategories.map((cat) => (
                              <DropdownMenuItem
                                key={cat.id}
                                onSelect={() => categorizeItem(item.id, cat.id)}
                                className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left hover:bg-gray-50"
                              >
                                <span
                                  className={`w-3 h-3 rounded-full ${cat.color}`}
                                ></span>
                                <span className="text-gray-700">{cat.name}</span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}

                      {/* Item Actions Menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="p-1 transition-colors rounded opacity-0 hover:bg-white group-hover:opacity-100"
                            aria-label="Item actions menu"
                          >
                            <MoreHorizontal className="w-4 h-4 text-gray-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          className="z-50 w-48 py-2 bg-white border border-gray-200 rounded-lg shadow-lg"
                          sideOffset={8}
                          align="end"
                        >
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger className="flex items-center w-full gap-2 px-4 py-2 text-left text-gray-700 hover:bg-gray-50">
                              <Tag className="w-4 h-4" />
                              {t("categorize")}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="w-40 py-2 bg-white border border-gray-200 rounded-lg shadow-lg">
                              <DropdownMenuItem
                                onSelect={() => categorizeItem(item.id, null)}
                                className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50"
                              >
                                {t("remove_category")}
                              </DropdownMenuItem>
                              {availableCategories.map((cat) => (
                                <DropdownMenuItem
                                  key={cat.id}
                                  onSelect={() =>
                                    categorizeItem(item.id, cat.id)
                                  }
                                  className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left hover:bg-gray-50"
                                >
                                  <span
                                    className={`w-3 h-3 rounded-full ${cat.color}`}
                                  ></span>
                                  <span className="text-gray-700">
                                    {cat.name}
                                  </span>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>

                          {/* Custom Item Actions */}
                          {finalItemActions.map((action) => (
                            <DropdownMenuItem
                              key={action.id}
                              onSelect={() => executeAction(action, item.id)}
                              className={`w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 ${
                                action.dangerous
                                  ? "text-red-600 hover:bg-red-50"
                                  : "text-gray-700"
                              }`}
                            >
                              {React.createElement(
                                availableIcons[action.icon],
                                { className: "w-4 h-4" }
                              )}
                              {action.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>

                {/* Complete Checkbox */}
                <button
                  onClick={() => toggleComplete(item.id)}
                  className="p-1 flex items-center justify-center text-gray-400 transition-colors hover:text-primary active:scale-[0.97] self-center"
                  aria-label={
                    item.completed ? t("mark_incomplete") : t("mark_complete")
                  }
                >
                  {item.completed ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <Circle className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          );
        })}

        {items.length === 0 && (
          <div className="py-1 text-center text-gray-500">
            <div className="mb-1 text-sm">{t("no_items_yet")}</div>
            <div className="text-xs">{t("add_items_to_get_started")}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmartList;
